import { tr, usePartnerTranslation } from "../../../lib/language";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X, Plus, Info } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Checkbox } from "../../ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import {
  CONTACT_INFO_ERROR,
  containsContactInfo,
} from "../../../lib/productTextSafety";

/* ---------- Info icon with hover tooltip ---------- */
export const InfoTooltip = ({
  children,
  testid
}) => {
  usePartnerTranslation();
  return <TooltipProvider delayDuration={120}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" tabIndex={-1} data-testid={testid} aria-label={tr("More info")} className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#9BA1AB] outline-none transition-colors hover:text-[#0F1115] focus-visible:ring-2 focus-visible:ring-[#FF5757]/40">
          <Info className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-[280px] rounded-md border-[#EAECEF] bg-[#0F1115] px-3 py-2 text-xs leading-relaxed text-white">
        {children}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>;
};

/* ---------- Field row (label + helper + child) ---------- */
export const FieldRow = ({
  label,
  required = false,
  helper,
  error,
  counter,
  info,
  children,
  className,
  testid
}) => {
  usePartnerTranslation();
  return <div className={cn("space-y-1.5", className)} data-testid={testid} data-error-anchor={error ? "true" : undefined}>
    {label && <div className="flex items-baseline justify-between gap-2">
        <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1115]">
          <span>
            {tr(label)}
            {required && <span className="ms-0.5 text-[#FF5757]">*</span>}
          </span>
          {info && <InfoTooltip testid={testid ? `${testid}-info` : undefined}>{tr(info)}</InfoTooltip>}
        </label>
        {counter && <span className="text-xs text-[#9BA1AB]">{counter}</span>}
      </div>}
    {children}
    {error && <p className="text-xs font-medium text-[#9B0000]" data-testid={testid ? `${testid}-error` : undefined}>
        {tr(error)}
      </p>}
    {helper && !error && <p className="text-xs text-[#6B7280]">{tr(helper)}</p>}
  </div>;
};

/* ---------- Text input ---------- */
export const TextInput = ({
  value,
  onChange,
  placeholder,
  testid,
  error,
  type = "text",
  min,
  max,
  step,
  maxLength,
  inputMode,
  pattern,
  enforceNoContactInfo = false,
}) => {
  usePartnerTranslation();
  const contactError = enforceNoContactInfo && containsContactInfo(value);
  const displayError = error || (contactError ? CONTACT_INFO_ERROR : "");

  return (
    <div className="space-y-1">
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={tr(placeholder)}
        data-testid={testid}
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        inputMode={inputMode}
        pattern={pattern}
        className={cn(
          "humrahe-input",
          displayError && "border-[#9B0000] focus:border-[#9B0000]",
        )}
      />
      {contactError && !error && (
        <p className="text-xs font-medium text-[#9B0000]">{CONTACT_INFO_ERROR}</p>
      )}
    </div>
  );
};

/* ---------- Textarea ---------- */
export const TextArea = ({
  value,
  onChange,
  placeholder,
  rows = 4,
  testid,
  error,
  maxLength,
  enforceNoContactInfo = false,
}) => {
  usePartnerTranslation();
  const contactError = enforceNoContactInfo && containsContactInfo(value);
  const displayError = error || (contactError ? CONTACT_INFO_ERROR : "");

  return (
    <div className="space-y-1">
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={tr(placeholder)}
        rows={rows}
        data-testid={testid}
        maxLength={maxLength}
        className={cn(
          "humrahe-input min-h-[110px] resize-y leading-relaxed",
          displayError && "border-[#9B0000] focus:border-[#9B0000]",
        )}
      />
      {contactError && !error && (
        <p className="text-xs font-medium text-[#9B0000]">{CONTACT_INFO_ERROR}</p>
      )}
    </div>
  );
};

/* ---------- Select (single) ---------- */
export const SelectField = ({
  value,
  onChange,
  options,
  placeholder = "Select…",
  testid,
  error,
  searchable = false,
  virtualized = false,
  searchPlaceholder = "Search…",
}) => {
  usePartnerTranslation();
  // options can be string[] or {value,label,description?,isHeader?}[]
  const norm = (o) => (typeof o === "string" ? { value: o, label: o } : o);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrollTop, setScrollTop] = useState(0);
  const items = useMemo(() => options.map(norm), [options]);
  const current = items.find((i) => !i.isHeader && i.value === value);
  const hasDescriptions = items.some((i) => i.description);
  const canVirtualize = virtualized && !hasDescriptions && !items.some((i) => i.isHeader);
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!searchable || !q) return items;
    return items.filter((i) => !i.isHeader && tr(i.label).toLowerCase().includes(q));
  }, [items, query, searchable]);
  const rowHeight = 40;
  const viewportHeight = 300;
  const overscan = 6;
  const startIndex = canVirtualize
    ? Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    : 0;
  const endIndex = canVirtualize
    ? Math.min(
        filteredItems.length,
        Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan,
      )
    : filteredItems.length;
  const visibleItems = filteredItems.slice(startIndex, endIndex);
  const selectItem = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    setQuery("");
    setScrollTop(0);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setQuery("");
          setScrollTop(0);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          style={{ display: "flex" }}
          className={cn(
            "humrahe-input items-center justify-between gap-2 text-start",
            !current && "text-[#9BA1AB]",
            error && "border-[#9B0000] focus:border-[#9B0000]",
          )}
        >
          <span className="min-w-0 flex-1 truncate">
            {current ? tr(current.label) : tr(placeholder)}
          </span>
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#6B7280]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className={cn(
          "w-[var(--radix-dropdown-menu-trigger-width)] rounded-xl border border-[#EAECEF] p-1 shadow-lg",
          !searchable && "max-h-[360px] overflow-y-auto",
        )}
      >
        {searchable && (
          <div className="sticky top-0 z-10 border-b border-[#F0F2F5] bg-white p-1.5">
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setScrollTop(0);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={tr(searchPlaceholder)}
              data-testid={testid ? `${testid}-search` : undefined}
              className="h-9 w-full rounded-md border border-[#E6E8EC] px-3 text-sm outline-none transition-colors placeholder:text-[#9BA1AB] focus:border-[#FF5757]"
            />
          </div>
        )}
        {filteredItems.length === 0 && (
          <div className="px-3 py-8 text-center text-sm text-[#9BA1AB]">{tr("No options found.")}</div>
        )}
        <div
          className={cn(searchable && "overflow-y-auto")}
          style={searchable ? { maxHeight: viewportHeight } : undefined}
          onScroll={(e) => canVirtualize && setScrollTop(e.currentTarget.scrollTop)}
        >
          <div
            style={
              canVirtualize
                ? {
                    height: filteredItems.length * rowHeight,
                    position: "relative",
                  }
                : undefined
            }
          >
        {visibleItems.map((it, idx) => {
          const itemIndex = startIndex + idx;
          if (it.isHeader) {
            return (
              <div
                key={`__h-${itemIndex}-${tr(it.label)}`}
                data-testid={testid ? `${testid}-header-${tr(it.label)}` : undefined}
                className={cn(
                  "select-none px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#9BA1AB]",
                  idx > 0 && "mt-1 border-t border-[#F0F2F5] pt-2",
                )}
              >
                {tr(it.label)}
              </div>
            );
          }
          const active = it.value === value;
          const itemContent = (
            <>
              <span className="min-w-0 flex-1">
                <span className="block text-[#0F1115]">{tr(it.label)}</span>
                {it.description && (
                  <span className="mt-0.5 block text-xs font-normal leading-snug text-[#6B7280]">
                    {tr(it.description)}
                  </span>
                )}
              </span>
              {active && (
                <Check className="mt-1 h-4 w-4 flex-shrink-0 text-[#FF5757]" />
              )}
            </>
          );
          return canVirtualize ? (
            <button
              type="button"
              key={it.value}
              data-testid={testid ? `${testid}-${it.value}` : undefined}
              onClick={() => selectItem(it.value)}
              className={cn(
                "absolute start-0 end-0 flex items-start justify-between gap-2 rounded-md px-3 py-2 text-start text-sm font-medium hover:bg-accent focus:bg-accent focus:outline-none",
                active && "bg-[#FFF7F7]",
              )}
              style={{
                height: rowHeight,
                transform: `translateY(${itemIndex * rowHeight}px)`,
              }}
            >
              {itemContent}
            </button>
          ) : (
            <DropdownMenuItem
              key={it.value}
              data-testid={testid ? `${testid}-${it.value}` : undefined}
              onClick={() => selectItem(it.value)}
              className={cn(
                "flex items-start justify-between gap-2 rounded-md px-3 text-sm font-medium",
                hasDescriptions ? "py-2.5" : "py-2",
              )}
            >
              {itemContent}
            </DropdownMenuItem>
          );
        })}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/* ---------- Multi-select chips ---------- */
export const MultiSelectChips = ({ values = [], onChange, options, testid }) => {
  usePartnerTranslation();
  const set = new Set(values);
  const toggle = (v) => {
    if (set.has(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const v = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? opt : opt.label;
        const active = set.has(v);
        return (
          <button
            type="button"
            key={v}
            data-testid={testid ? `${testid}-${v}` : undefined}
            onClick={() => toggle(v)}
            aria-pressed={active}
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors",
              active
                ? "border-[#FF5757] bg-[#FFEFEF] text-[#9B0000]"
                : "border-[#E6E8EC] bg-white text-[#0F1115] hover:border-[#0F1115]",
            )}
          >
            {active && <Check className="h-3 w-3" strokeWidth={2.4} />}
            {tr(label)}
          </button>
        );
      })}
    </div>
  );
};

/* ---------- Compact vertical checkbox list (replaces chips for long lists) ---------- */
export const CompactCheckList = ({
  values = [],
  onChange,
  options,
  columns = 2,
  testid,
}) => {
  usePartnerTranslation();
  const set = new Set(values);
  const toggle = (v) => {
    if (set.has(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };
  const colsClass =
    columns === 1
      ? "grid-cols-1"
      : columns === 3
        ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
        : "grid-cols-1 sm:grid-cols-2";
  return (
    <div
      data-testid={testid}
      className={cn(
        "grid gap-x-3 gap-y-1 rounded-lg border border-[#F0F2F5] bg-white p-2",
        colsClass,
      )}
    >
      {options.map((opt) => {
        const v = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? opt : opt.label;
        const active = set.has(v);
        return (
          <label
            key={v}
            data-testid={testid ? `${testid}-${v}` : undefined}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
              active
                ? "bg-[#FFEFEF] text-[#0F1115]"
                : "text-[#0F1115] hover:bg-[#FAFAFB]",
            )}
          >
            <Checkbox
              checked={active}
              onCheckedChange={() => toggle(v)}
              data-testid={testid ? `${testid}-${v}-input` : undefined}
            />
            <span className="flex-1">{tr(label)}</span>
          </label>
        );
      })}
    </div>
  );
};

/* ---------- Searchable multi-select dropdown ---------- */
export const SearchableMultiSelect = ({
  values = [],
  onChange,
  options,
  placeholder = "Select options",
  searchPlaceholder = "Search…",
  testid,
  error,
  disabledValues = [],
}) => {
  usePartnerTranslation();
  const items = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt,
  );
  const selectedSet = new Set(values);
  const disabledSet = new Set(disabledValues);
  const selectedItems = items.filter((item) => selectedSet.has(item.value));
  const [open, setOpen] = React.useState(false);

  const toggle = (value) => {
    if (disabledSet.has(value)) return;
    if (selectedSet.has(value)) {
      onChange(values.filter((item) => item !== value));
    } else {
      onChange([...values, value]);
    }
  };

  const remove = (value) => {
    onChange(values.filter((item) => item !== value));
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid ? `${testid}-trigger` : undefined}
            aria-expanded={open}
            style={{ display: "flex" }}
            className={cn(
              "humrahe-input min-h-[44px] w-full items-center gap-2 text-start",
              !selectedItems.length && "text-[#9BA1AB]",
              error && "border-[#9B0000] focus:border-[#9B0000]",
            )}
          >
            <span className="min-w-0 flex-1 truncate">
              {selectedItems.length
                ? tr("{{value0}} selected", { value0: selectedItems.length })
                : placeholder}
            </span>
            <ChevronDown className="ms-auto h-4 w-4 flex-shrink-0 text-[#6B7280]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] rounded-xl border border-[#EAECEF] p-0 shadow-lg"
        >
          <Command className="rounded-xl">
            <CommandInput
              placeholder={tr(searchPlaceholder)}
              data-testid={testid ? `${testid}-search` : undefined}
            />
            <CommandList className="max-h-[min(360px,55vh)]">
              <CommandEmpty>{tr("No languages found.")}</CommandEmpty>
              <CommandGroup>
                {items.map((item) => {
                  const active = selectedSet.has(item.value);
                  const disabled = disabledSet.has(item.value);
                  return (
                    <CommandItem
                      key={item.value}
                      value={item.label}
                      onSelect={() => toggle(item.value)}
                      disabled={disabled}
                      data-testid={testid ? `${testid}-${item.value}` : undefined}
                      className={cn(
                        "flex min-h-[40px] cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm",
                        disabled && "cursor-not-allowed opacity-45",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border",
                          active
                            ? "border-[#FF5757] bg-[#FF5757] text-white"
                            : "border-[#CFD2D8] bg-white",
                        )}
                        aria-hidden="true"
                      >
                        {active && <Check className="h-3 w-3" strokeWidth={2.6} />}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium text-[#0F1115]">
                        {item.label}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedItems.length > 0 && (
        <div
          className="flex flex-wrap gap-2"
          data-testid={testid ? `${testid}-selected` : undefined}
        >
          {selectedItems.map((item) => (
            <span
              key={item.value}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[#D8E4F8] bg-[#F5F8FF] px-2.5 py-1 text-xs font-semibold text-[#315A8A]"
            >
              <span className="truncate">{item.label}</span>
              <button
                type="button"
                onClick={() => remove(item.value)}
                aria-label={tr("Remove {{value0}}", { value0: item.label })}
                data-testid={testid ? `${testid}-${item.value}-remove` : undefined}
                className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[#587AA6] hover:bg-[#E7EFFD]"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Radio cards (horizontal) ---------- */
export const RadioCards = ({
  value,
  onChange,
  options,
  testid
}) => {
  usePartnerTranslation();
  return <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid={testid}>
    {options.map(opt => {
      const v = typeof opt === "string" ? opt : opt.value;
      const label = typeof opt === "string" ? opt : opt.label;
      const desc = typeof opt === "string" ? null : opt.description;
      const active = value === v;
      return <button type="button" key={v} data-testid={testid ? `${testid}-${v}` : undefined} onClick={() => onChange(v)} aria-pressed={active} className={cn("flex items-start gap-3 rounded-lg border bg-white px-3.5 py-2.5 text-start transition-colors", active ? "border-[#FF5757] bg-[#FFFAFA]" : "border-[#F0F2F5] hover:border-[#CFD2D8]")}>
          <span className={cn("mt-1 inline-block h-3.5 w-3.5 flex-shrink-0 rounded-full border", active ? "border-[#FF5757] bg-[#FF5757] ring-2 ring-white" : "border-[#CFD2D8]")} />
          <span className="space-y-0.5">
            <span className="block text-sm font-semibold text-[#0F1115]">{tr(label)}</span>
            {desc && <span className="block text-xs text-[#6B7280]">{desc}</span>}
          </span>
        </button>;
    })}
  </div>;
};

/* ---------- Checkbox row ---------- */
export const CheckboxField = ({
  value,
  onChange,
  label,
  testid,
  error
}) => {
  usePartnerTranslation();
  return <div className="space-y-1.5" data-testid={testid}>
    <label className="inline-flex cursor-pointer items-start gap-2.5 text-sm font-medium text-[#0F1115]">
      <Checkbox checked={!!value} onCheckedChange={c => onChange(!!c)} data-testid={testid ? `${testid}-input` : undefined} className={cn("mt-0.5", error && "border-[#9B0000]")} />
      <span className="flex-1">{tr(label)}</span>
    </label>
    {error && <p className="ps-7 text-xs font-semibold text-[#9B0000]">{tr(error)}</p>}
  </div>;
};

/* ---------- Repeater (array of items with add/remove) ---------- */
export const Repeater = ({ items, onChange, renderItem, addLabel = "Add", emptyState, testid }) => {
  usePartnerTranslation();
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, next) => onChange(items.map((it, idx) => (idx === i ? next : it)));
  const add = (template) => onChange([...items, template]);
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {items.length === 0 && emptyState && (
        <p className="text-xs italic text-[#9BA1AB]">{emptyState}</p>
      )}
      {items.map((item, idx) => (
        <div
          key={idx}
          className="relative rounded-lg border border-[#E6E8EC] bg-white p-3 pe-10"
          data-testid={testid ? `${testid}-row-${idx}` : undefined}
        >
          {renderItem(item, (next) => update(idx, next), idx)}
          <button
            type="button"
            onClick={() => remove(idx)}
            data-testid={testid ? `${testid}-remove-${idx}` : undefined}
            aria-label={tr("Remove")}
            className="absolute end-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FFEFEF] hover:text-[#9B0000]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => add(renderItem.template ? renderItem.template() : {})}
        data-testid={testid ? `${testid}-add` : undefined}
        className="inline-flex h-9 items-center gap-1.5 rounded-md border border-dashed border-[#CFD2D8] bg-white px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] hover:bg-[#FAFAFB]"
      >
        <Plus className="h-3.5 w-3.5" />
        {addLabel}
      </button>
    </div>
  );
};

/* ---------- Multi text input (chips you type with Enter) ---------- */
export const TagInput = ({
  values = [],
  onChange,
  placeholder,
  testid,
  enforceNoContactInfo = false,
}) => {
  usePartnerTranslation();
  const ref = useRef(null);
  const [draft, setDraft] = useState("");
  const draftContactError = enforceNoContactInfo && containsContactInfo(draft);
  const valueContactError = enforceNoContactInfo && values.some(containsContactInfo);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const v = draft.trim().replace(/,$/, "");
      if (enforceNoContactInfo && containsContactInfo(v)) return;
      if (v && !values.includes(v)) onChange([...values, v]);
      setDraft("");
    } else if (e.key === "Backspace" && !draft && values.length) {
      remove(values.length - 1);
    }
  };
  return (
    <div className="space-y-1">
      <div
        onClick={() => ref.current?.focus()}
        data-testid={testid}
        className={cn(
          "flex w-full cursor-text flex-wrap items-center gap-1.5 rounded-lg border border-[#D7DAE0] bg-white px-3 py-2 text-sm text-[#0F1115] outline-none transition-colors focus-within:border-[#FF5757] focus-within:shadow-[0_0_0_4px_rgba(255,87,87,0.1)]",
          (draftContactError || valueContactError) && "border-[#9B0000] focus-within:border-[#9B0000]",
        )}
        style={{ minHeight: 44 }}
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            data-testid={testid ? `${testid}-chip-${i}` : undefined}
            className="inline-flex h-7 items-center gap-1 rounded-full bg-[#F3EEEE] px-2.5 text-xs font-medium text-[#0F1115]"
          >
            {v}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(i);
              }}
              aria-label={tr("Remove {{value0}}", { value0: v })}
              className="text-[#6B7280] hover:text-[#9B0000]"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={ref}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={values.length ? "" : placeholder}
          className="min-w-[120px] flex-1 border-0 bg-transparent py-1 text-sm outline-none placeholder:text-[#B6BAC2]"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      </div>
      {(draftContactError || valueContactError) && (
        <p className="text-xs font-medium text-[#9B0000]">{CONTACT_INFO_ERROR}</p>
      )}
    </div>
  );
};

/* ---------- Bullet text area (one bullet per line, char-capped) ---------- */
export const BulletTextArea = ({
  value = "",
  onChange,
  placeholder,
  rows = 5,
  maxLength = 150,
  testid,
  error,
  enforceNoContactInfo = false,
}) => {
  usePartnerTranslation();
  const stripBulletMarkers = (text, preserveTrailingLine = false) => {
    const raw = String(text || "");
    const hadTrailingLine = preserveTrailingLine && raw.endsWith("\n");
    const cleaned = raw
      .split(/\r?\n/)
      .map((line) => line.replace(/^\s*[•*-]\s*/, ""))
      .filter((line) => line.trim().length > 0)
      .join("\n");
    return hadTrailingLine && cleaned ? `${cleaned}\n` : cleaned;
  };
  const displayValue = stripBulletMarkers(value, true);
  const len = displayValue.length;
  const contactError = enforceNoContactInfo && containsContactInfo(displayValue);
  const displayError = error || (contactError ? CONTACT_INFO_ERROR : "");
  const emptyHelper = `One entry per line · up to ${maxLength} characters.`;
  const counter = `${len} / ${maxLength}`;
  const helper =
    len === 0
      ? emptyHelper
      : len > maxLength
        ? `${len - maxLength} characters over the limit.`
        : counter;

  const handleChange = (e) => {
    onChange(stripBulletMarkers(e.target.value, true).slice(0, maxLength));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const ta = e.currentTarget;
      const pos = ta.selectionStart;
      const lineStart = ta.value.lastIndexOf("\n", pos - 1) + 1;
      const lineEnd = ta.value.indexOf("\n", pos);
      const currentLine = ta.value.slice(lineStart, lineEnd === -1 ? ta.value.length : lineEnd);
      if (!currentLine.trim()) {
        e.preventDefault();
      }
    }
  };

  return (
    <div className="space-y-1.5">
      <textarea
        value={displayValue}
        onChange={handleChange}
        onBlur={() => onChange(stripBulletMarkers(displayValue).slice(0, maxLength))}
        onKeyDown={handleKeyDown}
        placeholder={tr(placeholder)}
        rows={rows}
        maxLength={maxLength}
        data-testid={testid}
        className={cn(
          "humrahe-input w-full resize-y leading-6",
          displayError && "border-[#9B0000] focus:border-[#9B0000]",
        )}
        style={{ minHeight: 96 }}
      />
      <div
        className={cn(
          "flex min-h-[16px] items-center justify-between gap-3 text-[11px]",
          displayError || len > maxLength ? "text-[#9B0000]" : "text-[#9BA1AB]",
        )}
      >
        {displayError ? (
          <p className="font-medium">{displayError}</p>
        ) : (
          <>
            <p className="min-w-0 flex-1 truncate">{emptyHelper}</p>
            <p className="flex-shrink-0 tabular-nums">{helper === emptyHelper ? counter : helper}</p>
          </>
        )}
      </div>
    </div>
  );
};

/* ---------- Section header ---------- */
export const SectionHeader = ({
  title,
  subtitle
}) => {
  usePartnerTranslation();
  return <div className="mb-1">
    <h2 className="text-base font-extrabold tracking-tight text-[#0F1115]">{title}</h2>
    {subtitle && <p className="mt-0.5 text-xs text-[#6B7280]">{subtitle}</p>}
  </div>;
};

/* ---------- Auto-focus first input on mount (for steps) ---------- */
export const useAutofocusFirst = (deps) => {
  useEffect(() => {
    const el = document.querySelector(
      "main [data-testid=create-product-page] input:not([type=hidden]):not([disabled]), main [data-testid=create-product-page] textarea, main [data-testid=create-product-page] button[type=button]",
    );
    if (el) el.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
